import React from 'react';

// Status and battery tones resolve to the semantic tokens rather than fixed
// hex, so they follow the theme — and pick up the deeper light-mode ramp that
// keeps them legible on white.
function statusVar(status) {
  if (status === 'online') return 'var(--success-text)';
  if (status === 'idle') return 'var(--warning-text)';
  return 'var(--danger-text)';
}

function batteryVar(pct) {
  if (pct == null) return 'var(--text-muted)';
  if (pct > 50) return 'var(--success-text)';
  if (pct >= 20) return 'var(--warning-text)';
  return 'var(--danger-text)';
}

function statusLabel(status) {
  return status.charAt(0).toUpperCase() + status.slice(1);
}

const CloseIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
    aria-hidden="true">
    <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

function MetaRow({ label, value, color }) {
  return (
    <div className="meta-row">
      <span className="meta-label">{label}</span>
      <span className="meta-value" style={color ? { color } : undefined}>{value}</span>
    </div>
  );
}

export default function DeviceDetailDrawer({ device, onClose }) {
  const isOpen = !!device;

  return (
    <div
      className={`surface-float dm-drawer${isOpen ? ' open' : ''}`}
      // The landmark role and label only exist while the drawer is open. A
      // closed drawer carrying role + aria-label + aria-hidden is contradictory
      // markup — it announces a landmark and removes it in the same breath —
      // so when closed this is an inert, unlabelled box.
      {...(isOpen
        ? { role: 'complementary', 'aria-label': 'Device detail' }
        : { 'aria-hidden': true })}
    >
      {device && (
        <>
          {/* Header */}
          <div className="dm-drawer-header">
            {/* `name` resolves to the serial, so showing name over serial
                printed the same number twice. The design's second line is the
                Lightbug tracker id, which is the genuinely different
                identifier — and the one to quote when chasing a tracker. */}
            <div className="dm-drawer-header-info">
              <div className="dm-drawer-name">{device.name}</div>
              {device.deviceId && (
                <div className="dm-drawer-serial">Lightbug {device.deviceId}</div>
              )}
            </div>
            <button
              type="button"
              className="icon-btn-sq dm-drawer-close"
              onClick={onClose}
              aria-label="Close detail panel"
            >
              <CloseIcon />
            </button>
          </div>

          <div className="dm-drawer-body">
            {/* Status */}
            <div className="dm-drawer-section">
              <div className="dm-drawer-section-title">Status</div>
              <MetaRow label="Connection" value={statusLabel(device.status)} color={statusVar(device.status)} />
              <MetaRow label="Device type" value={device.type} />
              {device.client && <MetaRow label="Client" value={device.client} />}
              <MetaRow label="Location" value={device.location || '—'} />
              <MetaRow label="Last seen" value={device.lastSeen} />
            </div>

            {/* Telemetry */}
            <div className="dm-drawer-section">
              <div className="dm-drawer-section-title">Telemetry</div>

              <MetaRow
                label="Battery"
                value={device.battery != null ? `${device.battery}%` : '—'}
                color={batteryVar(device.battery)}
              />
              {device.battery != null && (
                <div
                  className="progress-wrap dm-battery-bar"
                  role="progressbar"
                  aria-label="Battery level"
                  aria-valuenow={device.battery}
                  aria-valuemin={0}
                  aria-valuemax={100}
                >
                  <div
                    className="progress-fill"
                    style={{
                      width: `${Math.min(device.battery, 100)}%`,
                      background: batteryVar(device.battery),
                    }}
                  />
                </div>
              )}

              <MetaRow label="Accuracy" value={device.accuracy != null ? `${device.accuracy} m` : '—'} />
              <MetaRow label="Fix type" value={device.fix || '—'} />
            </div>

            {/* Coordinates */}
            <div className="dm-drawer-section">
              <div className="dm-drawer-section-title">Coordinates</div>
              <div className="dm-coords-box">
                {device.lat != null && device.lng != null
                  ? `${device.lat.toFixed(6)}, ${device.lng.toFixed(6)}`
                  : 'No GPS data available'}
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="dm-drawer-actions">
            <button type="button" className="btn btn-primary">View History</button>
            <button type="button" className="btn btn-ghost">Locate Now</button>
          </div>
        </>
      )}
    </div>
  );
}
